/**
 * ActivateWaveModal — collects brand_name, product_hook, payment, etc.
 * and invokes the provided activate function (tentpole activations).
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, Send, Check, AlertCircle, Rocket } from 'lucide-react';
import { GENERATIONS, GENDERS } from '../../services/dsp';
import { useAuth } from '../../contexts/AuthContext';

const CARD = '#121212';
const BORDER = '#1E1E1E';
const BG = '#0A0A0A';
const T = '#00F5FF';
const P = '#FF6BBD';

const FIELD_CLASS = 'w-full px-3 py-2.5 rounded-lg text-xs text-white placeholder-gray-600 focus:outline-none';
const FIELD_STYLE = { background: BG, border: `1px solid ${BORDER}` };
const LABEL_CLASS = 'text-[9px] text-gray-500 uppercase tracking-wider font-semibold block mb-1';

export default function ActivateWaveModal({
  open, onOpenChange,
  title, subtitle,
  templateId, templateKind, // 'tentpole'
  matchingCreators = 0,
  activateFn, // async fn(payload) => response
  showPlatform = true,
  extraFilters = null, // React node with additional targeting UI (optional)
  defaultPlatform = '',
}) {
  const [brandName, setBrandName] = useState('');
  const [productHook, setProductHook] = useState('');
  const [payment, setPayment] = useState('350');
  const [platform, setPlatform] = useState(defaultPlatform);
  const [maxCreators, setMaxCreators] = useState('10');
  const [deadlineDays, setDeadlineDays] = useState('7');
  const [generation, setGeneration] = useState('');
  const [gender, setGender] = useState('');

  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [result, setResult] = useState(null);

  // Adaptive creator-scale presets — a single-location med-spa should see
  // 5/10/25/50, not 25/50/100/250. Driven by the brand_scale field captured
  // in onboarding (iter 52/53).
  const { user } = useAuth();
  const scalePresets = (() => {
    const s = user?.brand_scale || 'dtc_national';
    if (s === 'single_location') return [5, 10, 25, 50];
    if (s === 'multi_location')  return [10, 25, 50, 100];
    if (s === 'enterprise_national') return [100, 250, 500, 1000];
    return [25, 50, 100, 250];  // dtc_national fallback
  })();
  const [errorMsg, setErrorMsg] = useState(null);

  const reset = () => {
    setStatus('idle'); setResult(null); setErrorMsg(null);
  };

  const handleActivate = async () => {
    if (!brandName || !productHook || !payment) return;
    setStatus('loading'); setErrorMsg(null);
    const payload = {
      brand_name: brandName,
      template_id: templateId,
      product_hook: productHook,
      payment_per_creator: Number(payment),
      max_creators: Number(maxCreators),
      deadline_days: Number(deadlineDays),
    };
    if (platform) payload.platform = platform;
    if (generation) payload.generation_filter = generation;
    if (gender) payload.gender_filter = gender;
    try {
      const data = await activateFn(payload);
      setResult(data);
      setStatus('success');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : err.message || 'Activation failed');
      setStatus('error');
    }
  };

  const handleOpenChange = (next) => {
    if (status === 'loading') return;
    if (!next) reset();
    onOpenChange(next);
  };

  const canSubmit = brandName && productHook && Number(payment) > 0 && status !== 'loading';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl text-white" style={{ background: CARD, borderColor: BORDER }} data-testid="activate-wave-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><Rocket className="h-5 w-5" style={{ color: T }} />{title}</DialogTitle>
          <DialogDescription className="text-gray-500">{subtitle}</DialogDescription>
        </DialogHeader>

        {status === 'success' && result ? (
          <div className="py-6 text-center" data-testid="activation-success">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: '#4ade8020' }}>
              <Check className="h-7 w-7 text-green-400" />
            </div>
            <h3 className="text-lg font-black text-white mb-1">Wave Activated</h3>
            <p className="text-sm text-gray-400 mb-4">{result.message}</p>
            <div className="inline-grid grid-cols-2 gap-6 text-left">
              <div>
                <p className="text-[9px] text-gray-600 uppercase font-bold">Assignments Created</p>
                <p className="text-2xl font-black" style={{ color: T }}>{result.assignments_created}</p>
              </div>
              <div>
                <p className="text-[9px] text-gray-600 uppercase font-bold">Platform</p>
                <p className="text-2xl font-black text-white">{result.platform || '—'}</p>
              </div>
            </div>
            {result.creators?.length > 0 && (
              <div className="mt-4 text-left">
                <p className="text-[9px] text-gray-600 uppercase font-bold mb-1.5">Creators</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {result.creators.slice(0, 8).map((c, i) => (
                    <span key={`${c.handle}-${i}`} className="text-[10px] px-2 py-1 rounded-full font-semibold"
                      style={{ background: `${T}15`, color: T, border: `1px solid ${T}30` }}>{c.handle}</span>
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => handleOpenChange(false)} className="mt-5 px-5 py-2 rounded-lg text-xs font-bold text-black" style={{ background: T }}>Done</button>
          </div>
        ) : (
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            {matchingCreators > 0 && (
              <p className="text-[11px]" style={{ color: T }}>
                <strong>{matchingCreators}</strong> creators currently match this template.
              </p>
            )}

            <div>
              <label className={LABEL_CLASS}>Brand Name <span className="text-red-400">*</span></label>
              <input type="text" value={brandName} onChange={e => setBrandName(e.target.value)} placeholder="e.g., Your Brand"
                className={FIELD_CLASS} style={FIELD_STYLE} data-testid="brand-name-input" />
            </div>
            <div>
              <label className={LABEL_CLASS}>Product Hook <span className="text-red-400">*</span></label>
              <textarea value={productHook} onChange={e => setProductHook(e.target.value)} rows={2}
                placeholder="1 sentence that creators will weave into captions (e.g., 'New SPF 50 that doubles as a primer.')"
                className={`${FIELD_CLASS} resize-none`} style={FIELD_STYLE} data-testid="product-hook-input" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={LABEL_CLASS}>Payment / Creator</label>
                <input type="number" value={payment} onChange={e => setPayment(e.target.value)}
                  className={FIELD_CLASS} style={FIELD_STYLE} data-testid="payment-input" />
              </div>
              <div>
                <label className={LABEL_CLASS}>Max Creators</label>
                <input type="number" value={maxCreators} onChange={e => setMaxCreators(e.target.value)}
                  className={FIELD_CLASS} style={FIELD_STYLE} data-testid="max-creators-input" />
                <div className="flex gap-1 mt-1.5" data-testid="creator-scale-presets">
                  {scalePresets.map(n => {
                    // Projection model (client-side — for directional UX hint only):
                    //   reach   ≈ N × 150K avg per creator × 0.65 platform-dedupe factor
                    //   roas    ≈ tops out at ~6.4× for footprint-strong brand; diminishing above 100
                    const reach = (n * 150_000 * 0.65);
                    const reachStr = reach >= 1_000_000 ? `${(reach / 1e6).toFixed(1)}M` : `${(reach / 1e3).toFixed(0)}K`;
                    const roas = (3.2 + Math.min(3.2, n / 100)).toFixed(1);
                    const active = String(n) === String(maxCreators);
                    return (
                      <button
                        key={n} type="button"
                        onClick={() => setMaxCreators(String(n))}
                        data-testid={`creator-preset-${n}`}
                        className="flex-1 px-1.5 py-1 rounded-md text-[9px] font-bold transition-colors"
                        style={{
                          background: active ? T : 'transparent',
                          color: active ? '#0A0A0A' : '#9CA3AF',
                          border: `1px solid ${active ? T : BORDER}`,
                        }}
                        title={`~${reachStr} reach · ${roas}× ROAS projected`}
                      >
                        <div className="leading-tight">{n}</div>
                        <div className="text-[8px] opacity-70 leading-tight mt-0.5">{reachStr}·{roas}×</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className={LABEL_CLASS}>Deadline (days)</label>
                <input type="number" value={deadlineDays} onChange={e => setDeadlineDays(e.target.value)}
                  className={FIELD_CLASS} style={FIELD_STYLE} data-testid="deadline-input" />
              </div>
            </div>

            {showPlatform && (
              <div>
                <label className={LABEL_CLASS}>Platform (optional — leave blank to auto-select)</label>
                <select value={platform} onChange={e => setPlatform(e.target.value)} className={FIELD_CLASS} style={FIELD_STYLE} data-testid="platform-select">
                  <option value="">Auto-select</option>
                  <option value="TikTok">TikTok</option>
                  <option value="Instagram">Instagram</option>
                  <option value="YouTube">YouTube</option>
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLASS}>Generation</label>
                <select value={generation} onChange={e => setGeneration(e.target.value)} className={FIELD_CLASS} style={FIELD_STYLE} data-testid="generation-select">
                  <option value="">Any</option>
                  {GENERATIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL_CLASS}>Gender</label>
                <select value={gender} onChange={e => setGender(e.target.value)} className={FIELD_CLASS} style={FIELD_STYLE} data-testid="gender-select">
                  <option value="">Any</option>
                  {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
            </div>

            {extraFilters}

            {errorMsg && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2" data-testid="activation-error">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                <p className="text-xs text-red-400">{errorMsg}</p>
              </div>
            )}

            <button onClick={handleActivate} disabled={!canSubmit}
              className="w-full px-5 py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
              style={{ background: templateKind === 'tentpole' ? T : T, color: '#000', boxShadow: `0 0 20px ${T}35` }}
              data-testid="activate-submit-btn">
              {status === 'loading' ? <><Loader2 className="h-4 w-4 animate-spin" />Activating…</> : <><Send className="h-4 w-4" />Activate Wave</>}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
